import React, { useEffect, useRef, useState, useCallback } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import './AltProjects.css';

interface Project {
  id: string;
  title: string;
  videoPath: string;
  caseStudyUrl: string;
  duration: number; // Duration of the video segment to scrub (in seconds)
}

const projects: Project[] = [
  {
    id: 'superbowl',
    title: 'Designing for AI',
    videoPath: '/videos/compressed/superbowl.mp4',
    caseStudyUrl: '/designing-for-ai-at-google-translate',
    duration: 5,
  },
  {
    id: 'fish-in-ear',
    title: 'A Fish in Your Ear',
    videoPath: '/videos/compressed/fish-in-your-ear.mp4',
    caseStudyUrl: '/fish-in-your-ear',
    duration: 5,
  }
];

// Increase spacing and dot size for better performance
const DOT_SIZE = 5; // Base size of each dot in pixels
const DOT_SPACING = 10; // Spacing between dots in pixels
const FRAME_UPDATE_INTERVAL = 1000 / 15; // Update dots 15 times per second (not every frame for performance)
const SCROLL_SENSITIVITY = 0.005; // Seconds of video per scroll pixel

const AltProjects: React.FC = () => {
  const dotGridRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoCanvasRef = useRef<HTMLCanvasElement>(null);
  const lastFrameTimeRef = useRef<number>(0);
  const titleRefs = useRef<(HTMLHeadingElement | null)[]>([]);
  const lastScrollDeltaY = useRef<number>(0); // To store scroll direction for title animations
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [currentVideoTime, setCurrentVideoTime] = useState(0); // Time within the current project's video segment
  const [dots, setDots] = useState<{ x: number, y: number }[]>([]);
  const [isProcessingScroll, setIsProcessingScroll] = useState(false); // To manage scroll event processing frequency
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [isTransitioningProjects, setIsTransitioningProjects] = useState(false); // For GSAP title animations
  const navigate = useNavigate();
  
  // Calculate dot positions once on initialization
  useEffect(() => {
    const calculateDotPositions = () => {
      if (!dotGridRef.current || !canvasRef.current || !videoCanvasRef.current) return;
      
      const altProjectsEl = dotGridRef.current.closest('.alt-projects') as HTMLElement;
      if (!altProjectsEl) return;

      const width = altProjectsEl.offsetWidth;
      const height = altProjectsEl.offsetHeight;
      
      canvasRef.current.width = width;
      canvasRef.current.height = height;
      videoCanvasRef.current.width = width;
      videoCanvasRef.current.height = height;
      
      setCanvasSize({ width, height });
      
      const columns = Math.floor(width / DOT_SPACING);
      const rows = Math.floor(height / DOT_SPACING);
      const leftOffset = (width - (columns * DOT_SPACING)) / 2;
      const topOffset = (height - (rows * DOT_SPACING)) / 2;
      
      const newDots = Array.from({ length: rows * columns }, (_, i) => ({
        x: leftOffset + ((i % columns) * DOT_SPACING),
        y: topOffset + (Math.floor(i / columns) * DOT_SPACING),
      }));
      setDots(newDots);
    };
    
    calculateDotPositions();
    
    // Recalculate on resize but with debounce
    let resizeTimer: number;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(calculateDotPositions, 200);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);
  
  // Load videos and setup video elements
  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const project = projects[currentProjectIndex];

    // Determine start time based on transition direction if applicable
    let startTime = 0;
    if (lastScrollDeltaY.current < 0 && currentVideoTime > project.duration / 2) { // Check if scrolling up to previous & not just small adjustment
        //This implies we are likely coming from the *next* project by scrolling up
        //or we scrolled up to the end of the current one.
        //If isTransitioningProjects is true, it means we are switching projects.
        startTime = project.duration;
    } else if (isTransitioningProjects && lastScrollDeltaY.current < 0) {
        // Specifically handle scrolling up to a previous project
        startTime = project.duration; 
    }
    
    video.src = project.videoPath;
    video.muted = true;
    video.playsInline = true;
    video.pause();
    video.currentTime = startTime;
    setCurrentVideoTime(startTime);

    const onLoadedMetadata = () => {
      // Ensure video is at the correct starting time after loading metadata
      video.currentTime = currentVideoTime; 
    };

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.load(); // Start loading the new video

    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [currentProjectIndex]);
  
  // Update video's actual currentTime when our state changes
  useEffect(() => {
    if (videoRef.current && videoRef.current.readyState >= HTMLMediaElement.HAVE_METADATA) {
      videoRef.current.currentTime = currentVideoTime;
    }
  }, [currentVideoTime, currentProjectIndex]); // Ensure it runs if project changes too
  
  // Scroll handler
  useEffect(() => {
    const handleScroll = (e: WheelEvent) => {
      if (isTransitioningProjects || isProcessingScroll) return;

      setIsProcessingScroll(true);
      lastScrollDeltaY.current = e.deltaY; // Store scroll direction

      const project = projects[currentProjectIndex];
      let newVideoTime = currentVideoTime + e.deltaY * SCROLL_SENSITIVITY;

      if (newVideoTime > project.duration) {
        if (currentProjectIndex < projects.length - 1) {
          setIsTransitioningProjects(true);
          setCurrentProjectIndex(prev => prev + 1);
          // setCurrentVideoTime(0) handled by the project change useEffect
        } else {
          newVideoTime = project.duration; // Clamp to end of last video
          setCurrentVideoTime(project.duration);
        }
      } else if (newVideoTime < 0) {
        if (currentProjectIndex > 0) {
          setIsTransitioningProjects(true);
          const prevProject = projects[currentProjectIndex - 1];
          setCurrentProjectIndex(prev => prev - 1);
          // setCurrentVideoTime(prevProject.duration) handled by the project change useEffect, then set to actual prev duration
          // This ensures we effectively start at the end of the previous video
          // The video load useEffect will set currentTime to 0 first, then this will adjust after metadata
          // A dedicated effect for this might be cleaner, or set it directly after index change + delay
        } else {
          newVideoTime = 0; // Clamp to start of first video
          setCurrentVideoTime(0);
        }
      } else {
        setCurrentVideoTime(newVideoTime);
      }
      
      // Short timeout to allow the browser to process scroll and state updates smoothly
      setTimeout(() => setIsProcessingScroll(false), 50); 
    };

    window.addEventListener('wheel', handleScroll, { passive: false });
    return () => window.removeEventListener('wheel', handleScroll);
  }, [currentProjectIndex, currentVideoTime, isTransitioningProjects, isProcessingScroll]);
  
  // GSAP Title Animations & Resetting isTransitioningProjects
  useEffect(() => {
    if (isTransitioningProjects) {
      let titleOutIndex = -1;
      const scrollDirection = lastScrollDeltaY.current;

      if (scrollDirection > 0) { // Scrolling Down / Next project
        titleOutIndex = currentProjectIndex > 0 ? currentProjectIndex - 1 : -1; 
      } else if (scrollDirection < 0) { // Scrolling Up / Previous project
        titleOutIndex = currentProjectIndex < projects.length -1 ? currentProjectIndex + 1 : -1;
      } else { // Programmatic change (e.g. pagination click), assume forward
        // This part needs a bit more robust way if we want to know previous index for pagination
        // For now, assume pagination click implies moving to a new project, and animate out the one that is not currentProjectIndex
        // This logic might need refinement if pagination can jump multiple projects.
         titleRefs.current.forEach((_, idx) => {
            if (idx !== currentProjectIndex) titleOutIndex = idx;
         });
      }

      const titleOut = titleRefs.current[titleOutIndex];
      const titleIn = titleRefs.current[currentProjectIndex];

      const animateY = scrollDirection > 0 ? -100 : (scrollDirection < 0 ? 100 : (currentProjectIndex > titleOutIndex ? -100 : 100));
      const animateYFrom = scrollDirection > 0 ? 100 : (scrollDirection < 0 ? -100 : (currentProjectIndex > titleOutIndex ? 100 : -100));

      if (titleOut) {
        gsap.to(titleOut, { y: animateY, opacity: 0, duration: 0.4, ease: 'power2.inOut' });
      }
      if (titleIn) {
        gsap.fromTo(titleIn, { y: animateYFrom, opacity: 0 }, { y: 0, opacity: 1, duration: 0.4, delay: 0.1, ease: 'power2.out' });
      }
      setTimeout(() => setIsTransitioningProjects(false), 500); // Match animation + delay
    }
  }, [currentProjectIndex, isTransitioningProjects]); // Triggered by project index change when transitioning
  
  // Halftone animation loop
  const animateVideoHalftone = useCallback((timestamp: number) => {
    if (!canvasRef.current || !videoRef.current || !videoCanvasRef.current || dots.length === 0) {
      animationFrameRef.current = requestAnimationFrame(animateVideoHalftone);
      return;
    }

    const video = videoRef.current;
    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) { // Wait until video has data for current frame
      animationFrameRef.current = requestAnimationFrame(animateVideoHalftone);
      return;
    }

    const shouldUpdateFrame = timestamp - lastFrameTimeRef.current >= FRAME_UPDATE_INTERVAL;

    if (shouldUpdateFrame) {
      const mainCtx = canvasRef.current.getContext('2d');
      const vidCtx = videoCanvasRef.current.getContext('2d');
      if (!mainCtx || !vidCtx) return;

      const { width: canvasWidth, height: canvasHeight } = canvasSize;
      
      // Draw video to hidden canvas
      const videoRatio = video.videoWidth / video.videoHeight;
      const displayRatio = canvasWidth / canvasHeight;
      let drawWidth, drawHeight, offsetX, offsetY;
      if (videoRatio > displayRatio) {
        drawHeight = canvasHeight;
        drawWidth = drawHeight * videoRatio;
        offsetX = (canvasWidth - drawWidth) / 2;
        offsetY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = drawWidth / videoRatio;
        offsetX = 0;
        offsetY = (canvasHeight - drawHeight) / 2;
      }
      vidCtx.drawImage(video, offsetX, offsetY, drawWidth, drawHeight);

      // Calculate dot sizes and draw to main canvas
      mainCtx.clearRect(0, 0, canvasWidth, canvasHeight);
      dots.forEach(dot => {
        try {
          const pixel = vidCtx.getImageData(Math.floor(dot.x), Math.floor(dot.y), 1, 1).data;
          const brightness = (pixel[0] + pixel[1] + pixel[2]) / 3;
          const invertedBrightness = 255 - brightness;
          const minSize = 1;
          const maxSize = DOT_SIZE * 1.8; // Slightly larger max for more pronounced effect
          const size = minSize + ((invertedBrightness / 255) * (maxSize - minSize));
          mainCtx.beginPath();
          mainCtx.arc(dot.x, dot.y, size / 2, 0, Math.PI * 2);
          mainCtx.fillStyle = '#ccc'; // User updated color
          mainCtx.fill();
        } catch (e) { /* ignore getImageData errors if dot is off-canvas */ }
      });
      lastFrameTimeRef.current = timestamp;
    }
    animationFrameRef.current = requestAnimationFrame(animateVideoHalftone);
  }, [dots, canvasSize]); // DOT_SIZE is a const, no need to list

  // Start/Stop animation loop
  useEffect(() => {
    if (dots.length > 0 && canvasSize.width > 0) {
      lastFrameTimeRef.current = performance.now(); // Initialize for first frame
      animationFrameRef.current = requestAnimationFrame(animateVideoHalftone);
    } else {
       if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [animateVideoHalftone, dots.length, canvasSize.width]); // Rerun if animateVideoHalftone changes (due to its deps)

  const handleViewProject = () => {
    const project = projects[currentProjectIndex];
    if (project && project.caseStudyUrl) {
      navigate(project.caseStudyUrl);
    }
  };
  
  return (
    <div className="alt-projects">
      <Header />
      
      {/* Hidden video element for processing */}
      <video 
        ref={videoRef}
        className="hidden-video"
        loop
        muted
        playsInline
      />
      
      {/* Hidden canvas for video frame analysis */}
      <canvas ref={videoCanvasRef} className="hidden-canvas" />
      
      {/* Visible canvas for dot display */}
      <div className="dot-grid-container" ref={dotGridRef}>
        <canvas ref={canvasRef} className="dot-canvas"></canvas>
      </div>
      
      <div className="project-titles">
        {projects.map((project, index) => (
          <h1
            key={project.id}
            ref={(el: HTMLHeadingElement | null) => {
              titleRefs.current[index] = el;
            }}
            className={`project-title ${index === currentProjectIndex && !isTransitioningProjects ? 'active' : ''}`}
            style={{ opacity: index === currentProjectIndex && !isTransitioningProjects ? 1 : 0 }}
          >
            {project.title}
          </h1>
        ))}
      </div>
      
      <button className="view-project-button" onClick={handleViewProject}>
        View Project
      </button>
      
      <div className="pagination">
        {projects.map((_, index) => (
          <div
            key={projects[index].id}
            className={`pagination-dot ${index === currentProjectIndex ? 'active' : ''}`}
            onClick={() => {
              if (index !== currentProjectIndex) {
                lastScrollDeltaY.current = index > currentProjectIndex ? 1 : -1; // Simulate scroll direction for title animation
                setIsTransitioningProjects(true);
                setCurrentProjectIndex(index);
                setCurrentVideoTime(0); // Start new project from beginning
              }
            }}
          ><div className="pagination-dot-inner" /></div>
        ))}
      </div>
      
      <Footer />
    </div>
  );
};

export default AltProjects; 